import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TeamAvatar, ShareDialog } from "@/components/common";
import ImageUpload from "@/components/ImageUpload";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, QrCode, Users2, GripVertical, Shuffle, Save, KeyRound, Link2 } from "lucide-react";
import GroupAssignment from "@/components/manage/GroupAssignment";

export default function ManageTeams({ data, refresh }) {
  const { tournament: t, teams } = data;
  const [order, setOrder] = useState(teams);
  const [dialog, setDialog] = useState(null); // {mode:'add'|'edit', team}
  const [share, setShare] = useState(null); // {kind, team}
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteMax, setInviteMax] = useState(1);
  const [inviteBusy, setInviteBusy] = useState(false);

  React.useEffect(() => { setOrder(teams); }, [teams]);

  const onDragEnd = (res) => {
    if (!res.destination) return;
    const arr = Array.from(order);
    const [moved] = arr.splice(res.source.index, 1);
    arr.splice(res.destination.index, 0, moved);
    setOrder(arr);
  };

  const saveOrder = async () => {
    const payload = order.map((tm, i) => ({ id: tm.id, seed: i + 1, group: tm.group }));
    await api.put(`/tournaments/${t.id}/assignment`, { teams: payload });
    toast.success("Seeding saved");
    refresh();
  };

  const randomize = async () => {
    await api.post(`/tournaments/${t.id}/randomize`);
    toast.success("Assignments randomized");
    refresh();
  };

  const remove = async (id) => {
    await api.delete(`/teams/${id}`);
    toast.success("Team removed");
    refresh();
  };

  const createInvite = async () => {
    setInviteBusy(true);
    try {
      const { data: invite } = await api.post(`/tournaments/${t.id}/team-invites`, { max_uses: inviteMax });
      setInviteOpen(false);
      setShare({ kind: "invite", invite });
      toast.success("Open team invitation created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setInviteBusy(false); }
  };

  const origin = window.location.origin;
  const isGroup = t.tournament_type === "group_elimination";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold">Teams</h2>
          <p className="text-sm text-muted-foreground">{teams.length} of {t.max_teams} teams{isGroup ? "" : " · drag to set seeding"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isGroup && <Button variant="outline" onClick={randomize} data-testid="randomize-teams"><Shuffle className="mr-1.5 h-4 w-4" /> Randomize</Button>}
          {!isGroup && <Button variant="outline" onClick={saveOrder} data-testid="save-seeding"><Save className="mr-1.5 h-4 w-4" /> Save order</Button>}
          <Button variant="outline" onClick={() => { setInviteMax(1); setInviteOpen(true); }} disabled={teams.length >= t.max_teams} data-testid="open-team-invite"><Link2 className="mr-1.5 h-4 w-4" /> Open invite</Button>
          <Button onClick={() => setDialog({ mode: "add" })} data-testid="add-team-button"><Plus className="mr-1.5 h-4 w-4" /> Add team</Button>
        </div>
      </div>

      {order.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Users2 className="h-8 w-8 text-muted-foreground" />
          <p className="text-muted-foreground">No teams yet. Add your first team to get started.</p>
        </Card>
      ) : isGroup ? (
        <div className="space-y-6">
          <div className="space-y-2">
            {order.map((tm) => (
              <Card key={tm.id} className="flex items-center gap-3 p-3" data-testid={`team-item-${tm.id}`}>
                <TeamAvatar team={tm} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{tm.name} {tm.group && <span className="ml-1 rounded bg-muted px-1.5 text-[10px] font-bold">Group {tm.group}</span>}</div>
                  <div className="text-xs text-muted-foreground">{tm.captain_name ? `Captain: ${tm.captain_name}` : "No captain"} · {(tm.members || []).length} members</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setShare({ kind: "captain", team: tm })} data-testid={`share-captain-${tm.id}`}><KeyRound className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => setShare({ kind: "team", team: tm })} data-testid={`share-team-${tm.id}`}><QrCode className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => setDialog({ mode: "edit", team: tm })} data-testid={`edit-team-${tm.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(tm.id)} data-testid={`delete-team-${tm.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </Card>
            ))}
          </div>
          <GroupAssignment tournament={t} teams={teams} refresh={refresh} />
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="teams">
            {(prov) => (
              <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-2">
                {order.map((tm, i) => (
                  <Draggable key={tm.id} draggableId={tm.id} index={i}>
                    {(p, snap) => (
                      <Card ref={p.innerRef} {...p.draggableProps}
                        className={`flex items-center gap-3 p-3 ${snap.isDragging ? "dnd-dragging" : ""}`}
                        data-testid={`team-item-${tm.id}`}>
                        <span {...p.dragHandleProps} className="cursor-grab text-muted-foreground"><GripVertical className="h-5 w-5" /></span>
                        <div className="flex h-6 w-6 items-center justify-center rounded bg-muted text-xs font-bold">{i + 1}</div>
                        <TeamAvatar team={tm} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-semibold">{tm.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {tm.captain_name ? `Captain: ${tm.captain_name}` : "No captain"} · {(tm.members || []).length} members
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => setShare({ kind: "captain", team: tm })} data-testid={`share-captain-${tm.id}`} title="Captain link"><KeyRound className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setShare({ kind: "team", team: tm })} data-testid={`share-team-${tm.id}`} title="Team link"><QrCode className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setDialog({ mode: "edit", team: tm })} data-testid={`edit-team-${tm.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(tm.id)} data-testid={`delete-team-${tm.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </Card>
                    )}
                  </Draggable>
                ))}
                {prov.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {dialog && <TeamDialog t={t} dialog={dialog} onClose={() => setDialog(null)} onSaved={refresh} />}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-sm" data-testid="team-invite-dialog">
          <DialogHeader><DialogTitle className="font-heading">Create open team invitation</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Anyone with this link can create a team and will automatically become that team's captain.</p>
          <div>
            <Label>Maximum teams for this link</Label>
            <Input type="number" min={1} max={Math.max(1, t.max_teams - teams.length)} value={inviteMax}
              onChange={(e) => setInviteMax(e.target.value === "" ? "" : parseInt(e.target.value, 10))}
              onBlur={() => inviteMax === "" && setInviteMax(1)} className="mt-1.5" data-testid="team-invite-max" />
            <p className="mt-1 text-xs text-muted-foreground">{t.max_teams - teams.length} team place(s) currently available.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={createInvite} disabled={inviteBusy || inviteMax === "" || inviteMax > t.max_teams - teams.length} data-testid="create-team-invite">{inviteBusy ? "Creating…" : "Create invite"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {share && (
        <ShareDialog
          open={!!share} onOpenChange={() => setShare(null)}
          title={share.kind === "invite" ? "Open team invitation" : share.kind === "captain" ? "Captain invitation" : "Team member access"}
          subtitle={share.kind === "invite" ? `This link can create up to ${share.invite.max_uses} team(s).` : share.kind === "captain" ? "The captain can manage members and view everything." : "Read-only access for team members."}
          url={share.kind === "invite" ? `${origin}/join/${share.invite.code}` : `${origin}/${share.kind === "captain" ? "c" : "m"}/${share.kind === "captain" ? share.team.captain_code : share.team.team_code}`}
          code={share.kind === "invite" ? share.invite.code : share.kind === "captain" ? share.team.captain_code : share.team.team_code}
        />
      )}
    </div>
  );
}

function TeamDialog({ t, dialog, onClose, onSaved }) {
  const edit = dialog.mode === "edit";
  const [form, setForm] = useState(edit ? {
    name: dialog.team.name, color: dialog.team.color || "#007AFF",
    captain_name: dialog.team.captain_name || "", logo_url: dialog.team.logo_url || null,
  } : { name: "", color: "#007AFF", captain_name: "", logo_url: null });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (edit) await api.put(`/teams/${dialog.team.id}`, form);
      else await api.post(`/tournaments/${t.id}/teams`, form);
      toast.success(edit ? "Team updated" : "Team created");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="team-dialog">
        <DialogHeader><DialogTitle className="font-heading">{edit ? "Edit team" : "Add team"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Team name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="team-name-input" /></div>
          <div><Label>Captain name</Label><Input value={form.captain_name} onChange={(e) => set("captain_name", e.target.value)} className="mt-1.5" placeholder="Optional" data-testid="team-captain-input" /></div>
          <div>
            <Label>Team color</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <input type="color" value={form.color} onChange={(e) => set("color", e.target.value)} className="h-10 w-14 rounded-md border" data-testid="team-color-input" />
              <Input value={form.color} onChange={(e) => set("color", e.target.value)} />
            </div>
          </div>
          <div><Label>Team logo</Label><div className="mt-1.5"><ImageUpload value={form.logo_url} onChange={(v) => set("logo_url", v)} testId="team-logo-upload" /></div></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !form.name.trim()} data-testid="save-team-button">{busy ? "Saving…" : "Save team"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
